/**
 * "WRITE FOR MOONX" - THE WELCOME PAGE (design 25, section 6; design 24, T22 D6).
 *
 * WHO SEES WHAT. A reader signed in with NO author row - the gate sends them here from any cabinet
 * screen (src/router.tsx, `gated`), the bare address included - reads "Write for MoonX" and two
 * buttons. The accent one is "Sign in to your profile" (the owner, 15 October 2026): an author who
 * lands here is signed in with another account than the one their profile is on, so it signs out
 * and opens the sign-in page. The second is "Become a new author".
 * Pressing it is the one act that makes the row on purpose (`becomeAuthor`, `POST /v1/authorship`;
 * design 25, P16): "a reader who merely signs in is not made an applicant by accident". The answer
 * is the session, so the page turns at once into the two steps an applicant has, and the cabinet
 * is open to them from that moment, empty. An applicant who already has a row - from "Getting
 * started" on the Overview - opens the steps straight away.
 *
 * THE TWO STEPS, IN THE OWNER'S ORDER AND WORDS, NEITHER REQUIRED. "Your author profile", then
 * "Payment details", each with "Continue" (to its page) and "Skip for now"; the steps cannot be
 * jumped ahead (no step marker is clickable). The end says "You are all set.", says where a skipped
 * step waits - "Getting started" on the Overview - and goes to the cabinet. Where the reader is in
 * the steps lives in the ADDRESS (./paths.ts), because "Continue" leaves the page: Back from the
 * profile comes back to Payment details, not to the first step.
 *
 * ITS OWN FRAME: the doors' card (`AuthShell`), with no cabinet sidebar - the reader it is first
 * for has no cabinet screen to be offered - inside the router's one `main` (`outsideTheShell`).
 *
 * THE DEFAULT BUILD - fixtures, nobody signed in - draws the reader's page, and pressing the
 * button shows the steps without sending anything, as every fixture screen behaves.
 */
import { Alert, Button, Group, Stack, Stepper, Text, Title } from '@mantine/core';
import { useLocation, useRouter } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { MouseEvent, ReactNode, RefObject } from 'react';
import { isLiveApiConfigured } from '../../config';
import type { ApiFailure } from '../../features/auth/api/failure';
import { AuthShell } from '../../features/auth/components/AuthShell';
import { CABINET_HOME_PATH, SIGN_IN_PATH } from '../../features/auth/navigation';
import { useAuthSession } from '../../features/auth/session/AuthSessionProvider';
import { cabinetIsReachable } from '../../features/auth/session/session-state';
import { tooManyRequests } from '../../features/cabinet/api/problem';
import { followsInApp } from '../../features/shell/in-app-navigation';
import { useTranslation } from '../../i18n';
import type { TFunction } from 'i18next';
import { APPLY_ROUTE } from '../apply';
import { PAYOUT_METHOD_PATH } from '../payments/PayoutMethodRoute';
import { welcomeAddressOf, welcomePlaceFrom } from './paths';
import type { WelcomePlace, WelcomeStep } from './paths';

/** Why a press did not go through: `becomeAuthor` refused, or the sign-out failed. */
type Refusal = ApiFailure | { readonly kind: 'sign-out-failed' };

export function WelcomeRoute() {
  return isLiveApiConfigured() ? <LiveWelcome /> : <FixtureWelcome />;
}

/** A live build: the session the provider holds, its `becomeAuthor` and its `signOut`. */
function LiveWelcome() {
  const { state, becomeAuthor, signOut } = useAuthSession();
  const router = useRouter();
  const onBecomeAuthor = useCallback(async (): Promise<Refusal | null> => {
    const result = await becomeAuthor();
    return result.ok ? null : result.failure;
  }, [becomeAuthor]);
  // Signed in with the wrong account: leave it, then sign in with the right one - the cabinet
  // foot's sign-out does the same (CabinetShell.tsx).
  const onSignIn = useCallback(async (): Promise<Refusal | null> => {
    try {
      await signOut();
      router.history.replace(SIGN_IN_PATH);
      return null;
    } catch {
      return { kind: 'sign-out-failed' };
    }
  }, [router, signOut]);
  return (
    <Welcome
      hasAuthorRow={cabinetIsReachable(state)}
      onBecomeAuthor={onBecomeAuthor}
      onSignIn={onSignIn}
    />
  );
}

/** The default build: nobody to make an author of, so pressing the button only turns the page. */
function FixtureWelcome() {
  const router = useRouter();
  const [became, setBecame] = useState(false);
  const onBecomeAuthor = useCallback((): Promise<Refusal | null> => {
    setBecame(true);
    return Promise.resolve(null);
  }, []);
  const onSignIn = useCallback((): Promise<Refusal | null> => {
    router.history.replace(SIGN_IN_PATH);
    return Promise.resolve(null);
  }, [router]);
  return <Welcome hasAuthorRow={became} onBecomeAuthor={onBecomeAuthor} onSignIn={onSignIn} />;
}

interface WelcomeProps {
  readonly hasAuthorRow: boolean;
  /** Makes the row. Resolves to `null` once it is made, else to why not. */
  readonly onBecomeAuthor: () => Promise<Refusal | null>;
  /** Signs out and opens `/sign-in`. Resolves to `null`, else to why not. */
  readonly onSignIn: () => Promise<Refusal | null>;
}

function Welcome({ hasAuthorRow, onBecomeAuthor, onSignIn }: WelcomeProps) {
  const { t } = useTranslation('auth');
  return (
    <AuthShell
      title={t('welcome.title')}
      standfirst={hasAuthorRow ? t('welcome.stepsStandfirst') : t('welcome.readerStandfirst')}
      withHelp={false}
    >
      {hasAuthorRow ? (
        <FirstSteps />
      ) : (
        <BecomeAnAuthor onBecomeAuthor={onBecomeAuthor} onSignIn={onSignIn} />
      )}
    </AuthShell>
  );
}

function BecomeAnAuthor({
  onBecomeAuthor,
  onSignIn,
}: Pick<WelcomeProps, 'onBecomeAuthor' | 'onSignIn'>) {
  const { t } = useTranslation('auth');
  const [pending, setPending] = useState<'become' | 'sign-in' | null>(null);
  const [refusal, setRefusal] = useState<{ which: 'become' | 'sign-in'; why: Refusal } | null>(
    null,
  );
  const run = (which: 'become' | 'sign-in', act: () => Promise<Refusal | null>) => {
    setPending(which);
    setRefusal(null);
    void act().then((why) => {
      setRefusal(why === null ? null : { which, why });
      setPending(null);
    });
  };
  return (
    <Stack gap="sm">
      {refusal === null ? null : (
        <Alert
          variant="light"
          role="alert"
          title={
            refusal.which === 'become'
              ? t('welcome.refusal.notAnAuthorYet')
              : t('welcome.refusal.stillSignedIn')
          }
        >
          <Text size="sm">{refusalWords(refusal.why, t)}</Text>
        </Alert>
      )}
      <Button
        radius="xl"
        fullWidth
        loading={pending === 'sign-in'}
        disabled={pending === 'become'}
        onClick={() => {
          run('sign-in', onSignIn);
        }}
      >
        {t('welcome.signIn')}
      </Button>
      <Button
        radius="xl"
        fullWidth
        variant="default"
        loading={pending === 'become'}
        disabled={pending === 'sign-in'}
        onClick={() => {
          run('become', onBecomeAuthor);
        }}
      >
        {t('welcome.becomeAuthor')}
      </Button>
    </Stack>
  );
}

/** Which step is active: the two steps, then the end. */
const ACTIVE: Readonly<Record<WelcomeStep, number>> = { profile: 0, payment: 1, done: 2 };

function FirstSteps() {
  const { t } = useTranslation('auth');
  const router = useRouter();
  const search = useLocation({ select: (location) => location.searchStr });
  const place = welcomePlaceFrom(search);
  const heading = useRef<HTMLHeadingElement>(null);

  // A step that changes - or appears where the pressed button was - takes the focus to its
  // heading, so a keyboard and a screen reader go on from the new step, not from the page's top.
  useEffect(() => {
    if (place.step !== 'profile' || document.activeElement === document.body) {
      heading.current?.focus();
    }
  }, [place.step]);

  const moveTo = useCallback(
    (next: WelcomePlace) => {
      router.history.replace(welcomeAddressOf(next));
    },
    [router],
  );
  const leaveFor = useCallback(
    (href: string, next: WelcomePlace) => {
      // The next step first, so Back from the step's page comes back to it.
      moveTo(next);
      router.history.push(href);
    },
    [moveTo, router],
  );

  return (
    <Stepper active={ACTIVE[place.step]} size="sm">
      <Stepper.Step label={t('welcome.profileTitle')}>
        <StepBody
          headingRef={heading}
          title={t('welcome.profileTitle')}
          text={t('welcome.profileText')}
        >
          <ContinueLink
            href={APPLY_ROUTE.path}
            onLeave={(href) => {
              leaveFor(href, { step: 'payment', skipped: place.skipped });
            }}
          />
          <SkipButton
            onSkip={() => {
              moveTo({ step: 'payment', skipped: true });
            }}
          />
        </StepBody>
      </Stepper.Step>
      <Stepper.Step label={t('welcome.paymentTitle')}>
        <StepBody
          headingRef={heading}
          title={t('welcome.paymentTitle')}
          text={t('welcome.paymentText')}
        >
          <ContinueLink
            href={PAYOUT_METHOD_PATH}
            onLeave={(href) => {
              leaveFor(href, { step: 'done', skipped: place.skipped });
            }}
          />
          <SkipButton
            onSkip={() => {
              moveTo({ step: 'done', skipped: true });
            }}
          />
        </StepBody>
      </Stepper.Step>
      <Stepper.Completed>
        <Stack gap="sm">
          <Title ref={heading} tabIndex={-1} order={2} size="h4">
            {t('welcome.allSet')}
          </Title>
          {place.skipped ? <Text size="sm">{t('welcome.skippedLine')}</Text> : null}
          <Group>
            <InAppButton href={CABINET_HOME_PATH}>{t('welcome.toCabinet')}</InAppButton>
          </Group>
        </Stack>
      </Stepper.Completed>
    </Stepper>
  );
}

function StepBody({
  headingRef,
  title,
  text,
  children,
}: {
  readonly headingRef: RefObject<HTMLHeadingElement | null>;
  readonly title: string;
  readonly text: string;
  readonly children: ReactNode;
}) {
  return (
    <Stack gap="sm">
      <Title ref={headingRef} tabIndex={-1} order={2} size="h4">
        {title}
      </Title>
      <Text size="sm">{text}</Text>
      <Group gap="sm">{children}</Group>
    </Stack>
  );
}

/** "Continue": a real link to the step's page, which first moves this page on to the next step. */
function ContinueLink({
  href,
  onLeave,
}: {
  readonly href: string;
  readonly onLeave: (href: string) => void;
}) {
  const { t } = useTranslation('auth');
  return (
    <Button
      component="a"
      href={href}
      radius="xl"
      onClick={(event: MouseEvent<HTMLAnchorElement>) => {
        if (!followsInApp(event)) {
          return;
        }
        event.preventDefault();
        onLeave(href);
      }}
    >
      {t('welcome.continue')}
    </Button>
  );
}

function SkipButton({ onSkip }: { readonly onSkip: () => void }) {
  const { t } = useTranslation('auth');
  return (
    <Button variant="default" radius="xl" onClick={onSkip}>
      {t('welcome.skip')}
    </Button>
  );
}

/** A real link to an address in the app, followed in place by a plain click. */
function InAppButton({ href, children }: { readonly href: string; readonly children: ReactNode }) {
  const router = useRouter();
  return (
    <Button
      component="a"
      href={href}
      radius="xl"
      onClick={(event: MouseEvent<HTMLAnchorElement>) => {
        if (!followsInApp(event)) {
          return;
        }
        event.preventDefault();
        router.history.push(href);
      }}
    >
      {children}
    </Button>
  );
}

/** Why a press did not go through, in the cabinet's words for each kind of answer. */
function refusalWords(refusal: Refusal, t: TFunction<'auth'>): string {
  switch (refusal.kind) {
    case 'sign-out-failed':
      return t('welcome.signOutFailed');
    case 'rate-limited':
      return tooManyRequests(refusal.retryAfterSeconds, t('welcome.refusal.tooManyRequests'));
    case 'unreachable':
      return t('welcome.refusal.unreachable');
    case 'identity-unavailable':
    case 'unexpected':
      return t('welcome.refusal.trouble');
    case 'unauthenticated':
      return t('welcome.refusal.unauthenticated');
    case 'refused':
      return t('welcome.refusal.refused');
  }
}
